/**
 * Pure parsers that extract the routing-rule trace and readiness reasons from
 * an order's event detailJson (§8.5 routing trace, §9 readiness) for the
 * OrderDrawer (§18 /queue/:orderId). The worker stores @cairn/core's
 * RoutingResult on the ROUTED event and a ReadinessResult (or its reasons) on
 * READINESS_PENDING / EXCEPTION events; both arrive over the wire as `unknown`,
 * so everything here is defensive. DOM-free → unit-tested in the node project.
 */
import type { OrderEvent } from "./api.js";

export interface RoutingTraceLine {
  ruleId: string;
  ruleName: string;
  priority?: number;
  matched: boolean;
}

export interface RoutingTrace {
  /** The winning rule, if any (null = fell through to the fallback). */
  matchedRuleId: string | null;
  matchedRuleName: string | null;
  carrierCode: string | null;
  serviceCode: string | null;
  exceptionReason: string | null;
  /** Ordered evaluation trace (empty when the worker didn't record one). */
  trace: RoutingTraceLine[];
}

export interface ReadinessReasonLine {
  ruleId: string;
  ruleName: string;
  effect: string; // REQUIRE | BLOCK
  message: string;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/** Newest-first list assumed; returns the first event of a given type. */
function firstEventOfType(events: OrderEvent[], type: string): OrderEvent | undefined {
  return events.find((e) => e.type === type);
}

/** Extract the routing trace from the (newest) ROUTED event. */
export function parseRoutingTrace(events: OrderEvent[]): RoutingTrace | null {
  const ev = firstEventOfType(events, "ROUTED");
  if (!ev) return null;
  const d = asRecord(ev.detailJson);
  if (!d) return null;

  const rawTrace = Array.isArray(d.trace) ? d.trace : [];
  const trace: RoutingTraceLine[] = [];
  for (const line of rawTrace) {
    const r = asRecord(line);
    if (!r) continue;
    const ruleId = str(r.ruleId);
    if (!ruleId) continue;
    trace.push({
      ruleId,
      ruleName: str(r.ruleName) ?? ruleId,
      priority: typeof r.priority === "number" ? r.priority : undefined,
      matched: r.matched === true,
    });
  }

  return {
    matchedRuleId: str(d.ruleId),
    matchedRuleName: str(d.ruleName),
    carrierCode: str(d.carrierCode),
    serviceCode: str(d.serviceCode),
    exceptionReason: str(d.exceptionReason),
    trace,
  };
}

/** One-line summary: "matched <ruleId> → <carrier>/<service>" (§ task spec). */
export function summarizeRouting(t: RoutingTrace | null): string | null {
  if (!t) return null;
  if (t.exceptionReason && !t.matchedRuleId) {
    return `no rule matched → ${t.exceptionReason}`;
  }
  const target =
    t.carrierCode || t.serviceCode
      ? `${t.carrierCode ?? "?"}/${t.serviceCode ?? "?"}`
      : "no carrier";
  const rule = t.matchedRuleName ?? t.matchedRuleId ?? "fallback";
  return `matched ${rule} → ${target}`;
}

/**
 * Collect readiness reasons across READINESS_PENDING + EXCEPTION events. The
 * detailJson may be a ReadinessResult ({ reasons: [...] }) or a bare reasons
 * array. De-duplicated by ruleId+message, preserving first-seen order.
 */
export function parseReadinessReasons(events: OrderEvent[]): ReadinessReasonLine[] {
  const out: ReadinessReasonLine[] = [];
  const seen = new Set<string>();

  for (const ev of events) {
    if (ev.type !== "READINESS_PENDING" && ev.type !== "EXCEPTION") continue;
    const d = asRecord(ev.detailJson);
    if (!d) continue;
    const arr = Array.isArray(d.reasons)
      ? d.reasons
      : Array.isArray(ev.detailJson)
        ? (ev.detailJson as unknown[])
        : [];
    for (const item of arr) {
      const r = asRecord(item);
      if (!r) continue;
      const ruleId = str(r.ruleId) ?? "";
      const message = str(r.message) ?? str(r.reason) ?? "";
      if (!message && !ruleId) continue;
      const key = `${ruleId}::${message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ruleId: ruleId || "—",
        ruleName: str(r.ruleName) ?? (ruleId || "—"),
        effect: str(r.effect) ?? "REQUIRE",
        message: message || ruleId,
      });
    }
  }
  return out;
}
